import { zodResolver } from '@hookform/resolvers/zod'
import { changePasswordSchema, profileSchema } from '@dyeatlas/shared'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { Card, Notice, PageHeading } from '@/components/ui/misc'
import { useCurrentUser, useRefreshCurrentUser } from '@/hooks/useCurrentUser'
import { api } from '@/lib/api'
import { authClient, authErrorMessage } from '@/lib/auth-client'
import { initialsOf } from '@/lib/utils'

type ProfileValues = z.input<typeof profileSchema>

export function Profile() {
  const { user } = useCurrentUser()
  const refreshUser = useRefreshCurrentUser()

  const form = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { firstName: '', lastName: '' },
  })

  // The session resolves after first paint, so seed the form once it arrives.
  useEffect(() => {
    if (user) form.reset({ firstName: user.firstName ?? '', lastName: user.lastName ?? '' })
  }, [user, form])

  const save = useMutation({
    mutationFn: (values: ProfileValues) => api.patch('/me', values),
    onSuccess: async () => {
      await refreshUser()
      toast.success('Profile updated.')
    },
    onError: () => toast.error('Could not save your profile.'),
  })

  return (
    <>
      <PageHeading title="My Profile" description="Manage your personal details and profile settings" />

      <Card className="p-7">
        <form onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="mb-7 flex size-20 items-center justify-center rounded-full bg-brand-soft font-display text-2xl font-semibold text-brand">
            {initialsOf(user?.name, user?.email)}
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <Field label="First name" htmlFor="firstName" error={form.formState.errors.firstName?.message}>
              <Input id="firstName" {...form.register('firstName')} />
            </Field>
            <Field label="Last name" htmlFor="lastName" error={form.formState.errors.lastName?.message}>
              <Input id="lastName" {...form.register('lastName')} />
            </Field>
            <Field label="Email address" htmlFor="email" hint="Contact an administrator to change your email address.">
              <Input id="email" value={user?.email ?? ''} disabled readOnly />
            </Field>
          </div>

          <div className="my-6 h-px bg-line" />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => form.reset({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '' })}
            >
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}

type PasswordValues = z.input<typeof changePasswordSchema>

export function ChangePassword() {
  const form = useForm<PasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    const { error } = await authClient.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      // Keep this browser signed in, sign every other device out.
      revokeOtherSessions: true,
    })

    if (error) {
      form.setError('currentPassword', { message: authErrorMessage(error) ?? 'Incorrect password' })
      return
    }

    form.reset()
    toast.success('Password updated. Other devices have been signed out.')
  })

  return (
    <>
      <PageHeading title="Change Password" description="Keep your account secure by updating your credential regularly" />

      <Card className="p-7">
        <form onSubmit={onSubmit}>
          <div className="grid gap-5 md:grid-cols-3">
            <Field
              label="Current password"
              htmlFor="currentPassword"
              error={form.formState.errors.currentPassword?.message}
            >
              <Input id="currentPassword" type="password" autoComplete="current-password" {...form.register('currentPassword')} />
            </Field>
            <Field
              label="New password"
              htmlFor="newPassword"
              error={form.formState.errors.newPassword?.message}
            >
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Enter new secure password"
                {...form.register('newPassword')}
              />
            </Field>
            <Field
              label="Confirm new password"
              htmlFor="confirmPassword"
              error={form.formState.errors.confirmPassword?.message}
            >
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Verify new secure password"
                {...form.register('confirmPassword')}
              />
            </Field>
          </div>

          <div className="mt-5">
            <Notice title="Security Requirements:">
              <ul className="mt-1 space-y-1">
                <li className="flex gap-2">
                  <span className="text-brand">•</span> Minimum of 10 characters in length
                </li>
                <li className="flex gap-2">
                  <span className="text-brand">•</span> Contain at least one uppercase letter (A-Z)
                </li>
                <li className="flex gap-2">
                  <span className="text-brand">•</span> Contain at least one numeric digit (0-9) or special symbol
                </li>
              </ul>
            </Notice>
          </div>

          <div className="my-6 h-px bg-line" />

          <div className="flex justify-end gap-2">
            <Button type="submit" loading={form.formState.isSubmitting}>
              Update Password
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}
